import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { RefreshOutlined } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import moment from "moment-timezone";
import esLocale from "moment/locale/es"; // Do not remove these imports needed for chart translation 
import ptLocale from "moment/locale/pt"; // Do not remove these imports needed for chart translation 
import { useTranslation } from 'react-i18next';
import useSetQuery, { useMultiSetQuery } from 'customHooks/useQuery';
import { useLAOGlobalContext } from 'context/LAOGlobalContext';
import { generateFilterPayload } from 'helpers/emeaHelpers';
import { boxShadow as defaultBoxShadow, chartsHeight } from 'helpers/styleHelpers';
import { laoGetCharts } from 'reduxLib/services/LaoServices/laoChartService';
import LaoBarChart from '../Charts/LaoBarChart';
import LaoDonutChart from '../Charts/LaoDonutChart';
import { SubheaderPattern1, SubheaderPattern2, SubheaderPattern3, SubheaderPattern4 } from '../LaoCardHeader';
import ShipmentContainerImportKPI from 'components/LAO/ShipmentContainerImportKPI';
import { LaoProcessChart } from '../LaoProcessChart';
import GroupedBarChart from 'components/D3Charts/GroupedBarChart';
import GroupedStackBarChart from 'components/D3Charts/GroupedStackBarChart';
import GroupedDataTable from 'components/common/Table/GroupedDataTable';
import GroupedMatrixTable from 'components/common/Table/GroupedMatrixTable';

const useStyles = makeStyles((theme) => ({
    text: {
        color: theme.palette.text.primary,
    },
    chartCard: {
        height: ({ height, legendGroup }) => legendGroup ? height + 35 : height,
        borderRadius: theme.spacing(3),
        boxShadow: ({ boxShadow }) => boxShadow,
        maxWidth: ({ maxWidth }) => maxWidth,
        minWidth: ({ minWidth }) => minWidth,
        background: theme.palette.card.base,
        display: 'flex',
        flexDirection: 'column',
    },
    paddingStyle: {
        paddingBottom: theme.spacing(3),
    },
    noData: {
        padding: theme.spacing(2.5),
    },
    noDataContainer: {
        justifyContent: "center",
        alignItems: "center",
        height: "80%"
    },
    countryRadioLeft: {
        '& .MuiGrid-item': {
            justifyContent: 'flex-start !important',
            textAlign: 'left !important',
        },
    }
}));

const LAOChartCard = ({
    page,
    subtype,
    subtype1,
    subtype2,
    subtype3,
    title,
    chart,
    trendline,
    showTotal,
    unitOfTotal,
    yLabel,
    legend,
    monthFilter,
    showMonthFilter = false,
    customDocDate = null,
    refresh = false,
    setRefresh,
    legendGroup,
    xKey = "state",
    testId,
    refreshIcon = true,
    defaultFilters,
    ...rest
}) => {
    const height = rest.height || chartsHeight;
    const classes = useStyles({
        boxShadow: rest.boxShadow || defaultBoxShadow,
        height,
        maxWidth: rest.maxWidth || 400,
        minWidth: rest.minWidth || 400,
        legendGroup,
    });

    const { language } = useLAOGlobalContext();

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [params] = useSetQuery();
    const setMultiParams = useMultiSetQuery();
    const [controller] = useState(() => new AbortController());
    const [total, setTotal] = useState(0);
    const [subheaderData, setSubheaderData] = useState({});
    const [chartData, setChartData] = useState([]);
    const [selectedValue, setSelectedValue] = useState(() => (subtype === "IMPORT_CONTAINER_FORECAST"
        ? rest.radioKey2
        : subtype1 === "LEAD_TIME_INTERNATIONAL_BY_MONTH"
            ? rest.radioKey1
            : rest.radioKey) || null);
    const radioValue = {
        [rest.radioKey]: subtype,
        [rest.radioKey1]: subtype1,
        [rest.radioKey2]: subtype2,
        [rest.radioKey3]: subtype3
    } 
    const { loading, data } = useSelector(({ laoCharts }) => laoCharts?.[radioValue?.[selectedValue] || subtype] || {});
    const selectedValueRadioVal = {
        daily: "DAILY",
        weekly: "WEEKLY",
        monthly: "MONTHLY"
    }
    const [selectedValue1, setSelectedValue1] = useState(() => rest.radioKey6);
    const [countryLeadTimeView, setCountryLeadTimeView] = useState('quarterly');

    const monthlySubcolumns = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const quarterlySubcolumns = ['Q1', 'Q2', 'Q3', 'Q4'];
    const leadTimeCountrySubcolumns = countryLeadTimeView === 'monthly' ? monthlySubcolumns : quarterlySubcolumns;

    const handleRadioValue = (value) => {
        setSelectedValue1(value);
    } 

    const subTypeVals = {
        // Nested object structure is followed to retain chart selection(bid/spot) while changing radio selection
        // radioValue: { currentsubtype: newsubtype }
        IMPORT_KPI_CARRIER_OP_CONTAINER: {
            "IMPORT_KPI_CARRIER_OP_SHIPMENT_SPOT": "IMPORT_KPI_CARRIER_OP_CONTAINER_SPOT",
            "IMPORT_KPI_CARRIER_OP_SHIPMENT_BID": "IMPORT_KPI_CARRIER_OP_CONTAINER_BID",
        },
        IMPORT_KPI_CARRIER_OP_SHIPMENT: {
            "IMPORT_KPI_CARRIER_OP_CONTAINER_SPOT": "IMPORT_KPI_CARRIER_OP_SHIPMENT_SPOT",
            "IMPORT_KPI_CARRIER_OP_CONTAINER_BID": "IMPORT_KPI_CARRIER_OP_SHIPMENT_BID",
        },
        IMPORT_KPI_CARRIER_CONTRACT_CONTAINER: {
            "IMPORT_KPI_CARRIER_OP_SHIPMENT_SPOT": "IMPORT_KPI_CARRIER_OP_CONTAINER_SPOT",
            "IMPORT_KPI_CARRIER_OP_SHIPMENT_BID": "IMPORT_KPI_CARRIER_OP_CONTAINER_BID",
        },
        IMPORT_KPI_CARRIER_CONTRACT_SHIPMENT: {
            "IMPORT_KPI_CARRIER_OP_CONTAINER_SPOT": "IMPORT_KPI_CARRIER_OP_SHIPMENT_SPOT",
            "IMPORT_KPI_CARRIER_OP_CONTAINER_BID": "IMPORT_KPI_CARRIER_OP_SHIPMENT_BID",
        }
    }
    const handleChange = (value) => {
        setSelectedValue(value);
        const selectedSubtype = radioValue?.[value] || subtype;
        const {
            searchSubType,
            // importInternationalShipperWithSeparator,
            ...prevFilters
        } = params.laoFilters;
        if (subTypeVals?.[selectedSubtype]?.[searchSubType]) {
            setMultiParams({
                laoFilters: JSON.stringify({
                    ...prevFilters,
                    searchSubType: subTypeVals?.[selectedSubtype]?.[searchSubType],
                }),
                isHyperlink: true
            });
        }
    };
    
    const getMetric = (obj, type, prefix, field) => {
        const map = {
            avg: `avg${prefix}${field}`,
            min: `min${prefix}${field}`,
            max: `max${prefix}${field}`
        };
        return obj[map[type]] ?? obj[`avg${prefix}${field}`];
    };

    useEffect(() => {
        if (data) {
            setTotal(data?.reduce((a, b) => a + b.totalCount, 0));
            switch (subtype || subtype1 || subtype2 || subtype3) {
                case 'IMPORT_KPI_PO_ITEMS_SHIPMENT':
                case 'IMPORT_KPI_SHIPMENT_CONTAINERS':
                case 'EXPORT_KPI_LINE_ITEMS_PLANNED':
                case 'EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS':
                    setChartData(data.map((d) => {
                        return {
                            ...d,
                            stateKey: d.state.toLowerCase(),
                        }
                    }));
                    break;
                case 'IMPORT_KPI_CONTAINER_RTD': {
                    setSubheaderData({
                        value1: data[0]?.cntrWithID,
                        value2: data[0]?.cntrWithGR,
                        value3: data[0]?.cntrWithoutGR
                    })
                    setChartData([
                        {
                            stateKey: "without_GR_with_RDD",
                            state: t("without_GR_with_RDD"),
                            stateDesc: t("without_GR_with_RDD"),
                            totalCount: data[0]?.cntrWithoutGRWithRDD

                        },
                        {
                            stateKey: "without_GR_without_RDD",
                            state: t("without_GR_without_RDD"),
                            stateDesc: t("without_GR_without_RDD"),
                            totalCount: data[0]?.cntrWithoutGRWithoutRDD
                        }
                    ])
                    break;
                }                    
                case 'IMPORT_KPI_CONTAINER_RDD':
                case 'EXPORT_KPI_EXPORT_UNITS_INVOICED': {
                    let processedData = data?.map(d => {
                        return {
                            ...d,
                            formattedStartDate: subtype === 'EXPORT_KPI_EXPORT_UNITS_INVOICED' ? moment(d.state, "MM-YY").format("MMM/YY") : moment(d.startDate, "YYYY-MM-DD").format("DD/MM ddd")
                        }
                    });
                    setChartData(processedData);
                    break;
                }
                case 'IMPORT_KPI_PO_ITEMS_DEVIATION': {
                    setSubheaderData({
                        value1: data[0]?.totalCount,
                        value2: data[1]?.totalCount,
                    })
                    setChartData([
                        {
                            stateKey: "po_with_deviation",
                            state: t("po_with_deviation"),
                            stateDesc: t("po_with_deviation"),
                            totalCount: data[2]?.totalCount
                        }
                    ])
                    break;
                }
                case 'IMPORT_KPI_CARRIER_OP_SHIPMENT':
                case 'IMPORT_KPI_CARRIER_OP_CONTAINER':
                case 'EXPORT_KPI_EXPORT_UNITS_BY_CARRIER': {
                    let processedData = data?.map(d => {
                        return {
                            ...d,
                            stateDesc: d.state.toUpperCase(),
                            totalCount: subtype === 'EXPORT_KPI_EXPORT_UNITS_BY_CARRIER' ? d.totalCount : d.totalBid + d.totalSpot,
                        }
                    });
                    setChartData(processedData);
                    break;
                }                    
                case 'IMPORT_KPI_CARRIER_CONTRACT_SHIPMENT':
                case 'IMPORT_KPI_CARRIER_CONTRACT_CONTAINER': {
                    let processData = data?.map(d => {
                        return {
                            ...d,
                            stateKey: d.state.toLowerCase(),
                            state: t(d.state.toLowerCase()),
                            stateDesc: t(d.stateDesc.toLowerCase())
                        }
                    });
                    setChartData(processData);
                    break;
                }                    
                case 'EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE': {
                    setSubheaderData({
                        value1: data.find(d => d.state === 'OCEAN')?.totalCount,
                        value2: data.find(d => d.state === 'LAND')?.totalCount,
                        value3: data.find(d => d.state === 'AIR')?.totalCount
                    })
                    setChartData(data.map((d) => {
                        return {
                            ...d,
                            stateKey: d.state.toLowerCase(),
                        }
                    }));
                    break;
                }                    
                case 'EXPORT_KPI_EXPORT_UNITS': {
                    setSubheaderData({
                        value1: data?.find(d => d.state === 'PO_ITEMS_WITH_UNIQUE_LD_NBR')?.totalCount,
                        value2: data?.find(d => d.state === 'LDS_INVOICED')?.totalCount,
                        value3: data?.find(d => d.state === 'LDS_BILLING_PENDING')?.totalCount
                    })
                    let processData = data?.filter((d) => d.hasOwnProperty("totalRatio")).map((d) => {
                        return {
                            ...d,
                            stateKey: d.state.toLowerCase(),
                            state: t(d.state.toLowerCase()),
                        }
                    });
                    setChartData(processData);
                    break;
                }                    
                case 'EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT': {
                    setSubheaderData({
                        value1: data?.find(d => d.state === 'LDS_INVOICED')?.totalCount,
                        value2: data?.find(d => d.state === 'LDS_INVOICED_WITH_SHIPMENT')?.totalCount,
                        value3: data?.find(d => d.state === 'LDS_INVOICED_WITHOUT_SHIPMENT')?.totalCount
                    })
                    let processData2 = data?.filter((d) => d.hasOwnProperty("totalRatio")).map((d) => {
                        return {
                            ...d,
                            stateKey: d.state.toLowerCase(),
                            state: t(d.state.toLowerCase()),
                        }
                    });
                    setChartData(processData2);
                    break;
                }                    
                case "TIMELINE_KPI_EXT_VENDORS_PO_LINE":
                case "TIMELINE_KPI_EXT_VENDORS_CONTAINERS":
                case "TIMELINE_KPI_EXT_VENDORS_SHIPMENTS":
                case "TIMELINE_KPI_INTER_COMPANY_PO_LINE":
                case "TIMELINE_KPI_INTER_COMPANY_CONTAINERS":
                case "TIMELINE_KPI_INTER_COMPANY_SHIPMENTS":
                case "TIMELINE_KPI_DIRECT_SHIPMENT_PO_LINE":
                case "TIMELINE_KPI_DIRECT_SHIPMENT_CONTAINERS":
                case "TIMELINE_KPI_DIRECT_SHIPMENT_SHIPMENTS": {
                    let processData = data?.map((d) => {
                        return {
                            ...d,
                            totalRatio: d?.totalCount > 0 && d?.totalRatio === 0 ? '<0.1' : d?.totalRatio,
                            state: d?.state?.toLowerCase(),
                        }
                    });
                    setChartData(processData);
                    break;
                }
                case 'IMPORT_CONTAINER_FORECAST': {
                    let processData = data?.map(d => {
                        return {
                            ...d,
                            formattedDate: moment(d.startDate, "YYYY-MM-DD").format("DD/MM ddd"),
                        }
                    });
                    setChartData(processData);
                    break;
                }                    
                case 'LEAD_TIME_INTERNATIONAL_BY_MONTH':
                case 'LEAD_TIME_ORIGIN_BY_MONTH':
                case 'LEAD_TIME_DESTINATION_BY_MONTH':
                case 'LEAD_TIME_TOTAL_BY_MONTH': {
                    let processData = [];

                    if (selectedValue === 'total') {
                        processData = data?.map(d => ({
                            ...d,
                            formattedStartDate: moment(d.previousStartDate, "YYYY-MM-DD").format("MMM"),

                            previous_origin: getMetric(d, selectedValue1, 'Previous', 'Origin'),
                            previous_dest: getMetric(d, selectedValue1, 'Previous', 'Dest'),
                            previous_international: getMetric(d, selectedValue1, 'Previous', 'International'),
                            previous_total: getMetric(d, selectedValue1, 'Previous', 'Total'),

                            current_origin: getMetric(d, selectedValue1, 'Current', 'Origin'),
                            current_dest: getMetric(d, selectedValue1, 'Current', 'Dest'),
                            current_international: getMetric(d, selectedValue1, 'Current', 'International'),
                            current_total: getMetric(d, selectedValue1, 'Current', 'Total'),
                        }));
                    }

                    if (['international', 'origin', 'destination'].includes(selectedValue)) {
                        processData = data?.map(d => ({
                            ...d,
                            formattedStartDate: moment(d.currentStartDate, "YYYY-MM-DD").format("MMM"),
                            currentLabel: moment(d.currentStartDate).format("YYYY"),
                            previousLabel: moment(d.previousStartDate).format("YYYY"),

                            current: getMetric(d, selectedValue1, 'Current', ''),
                            previous: getMetric(d, selectedValue1, 'Previous', ''),
                        }));
                    }
                    
                    setChartData(processData);
                    break;
                }                    
                case 'LEAD_TIME_BY_COUNTRY_QUARTER': {
                    setChartData(data);
                    break;               
                }
                case 'LEAD_TIME_BY_SHIPPER': {
                    let processData = data?.map(shipper => {
                        const translatedMetrics = {};
                        Object.keys(shipper.metrics || {}).forEach(month => {
                            // Parse month in English format, then format in target language
                            const monthIndex = moment(month, "MMM", 'en').month();
                            const translatedMonth = moment().month(monthIndex).locale(language).format("MMM");
                            translatedMetrics[translatedMonth] = shipper.metrics[month];
                        });
                        return {
                            ...shipper,
                            metrics: translatedMetrics
                        };
                    });
                    setChartData(processData);
                    break;
                }
            }
        }
    }, [data, subtype, subtype1, subtype2, subtype3, selectedValue1, selectedValue]);

    const subTypeSelection = {
        "daily": "IMPORT_CONTAINER_FORECAST",
        "weekly": "IMPORT_CONTAINER_FORECAST",
        "monthly": "IMPORT_CONTAINER_FORECAST",
        "container": "IMPORT_KPI_CARRIER_OP_CONTAINER",
        "shipment": "IMPORT_KPI_CARRIER_OP_SHIPMENT",
        "container1": "IMPORT_KPI_CARRIER_CONTRACT_CONTAINER",
        "shipment1": "IMPORT_KPI_CARRIER_CONTRACT_SHIPMENT",
        "international": "LEAD_TIME_INTERNATIONAL_BY_MONTH",
        "origin": "LEAD_TIME_ORIGIN_BY_MONTH",
        "destination": "LEAD_TIME_DESTINATION_BY_MONTH",
        "total": "LEAD_TIME_TOTAL_BY_MONTH",
    };
    const refreshChart = () => {
        if (!subtype) return;
        const tableFilters = params.laoFilters;
        const filterPayload = generateFilterPayload(defaultFilters);
        const requestedSubtype = selectedValue ? subTypeSelection[selectedValue] : subtype;
        const countryLeadTimeGranularity = countryLeadTimeView === 'monthly' ? 'MONTH' : 'QUARTER';
        let granularity;
        if (requestedSubtype === "IMPORT_CONTAINER_FORECAST") {
            granularity = selectedValueRadioVal[selectedValue];
        } else if (requestedSubtype === "LEAD_TIME_BY_COUNTRY_QUARTER") {
            granularity = countryLeadTimeGranularity;
        }

        const payload = {
            ...filterPayload,
            importEtaRange: subtype === "IMPORT_CONTAINER_FORECAST"
                ? filterPayload?.importEtaRange || {
                    "startTime": new Date().toISOString().split("T")[0],
                    "endTime": null
                } : filterPayload?.importEtaRange,
            granularity
        }
        dispatch(laoGetCharts({
            controller,
            page,
            region: "LAO",
            subtype: requestedSubtype,
            body: {
                ...payload,
                ...tableFilters,
            }
        }));
    }
    useDeepCompareEffect(() => {
        if (refresh) {
            refreshChart();
            setRefresh(false);
        }
    }, [refresh, selectedValue, countryLeadTimeView]);

    const refreshAction = () => {
        refreshChart();
    }
    useDeepCompareEffect(() => {
        if (!params.loadView) {
            refreshChart();
            moment.locale(language);
        }
    }, [subtype, params.loadView, params.laoFilters, language, selectedValue, countryLeadTimeView]);

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);
    const chartProps = {
        ...rest,
        data: chartData,
        loading,
        height: height - 100,
        xKey,
        keys: rest.keys || ["totalCount"],
        yKey: rest.yKey || "totalCount",
        themeType: "dark",
        trendline,
        yLabel,
        subtype,
        legend,
        legendGroup,
        testId,
        dx: "-11%",
        valueType: selectedValue === rest?.radioKey1 ? subtype1 : subtype,
        stackGroups: rest.stackGroups,
        getGroupLabel: rest.getGroupLabel,
        legendKeyMap: {
            previous: "previousLabel",
            current: "currentLabel"
        },
        subcolumns: subtype === 'LEAD_TIME_BY_COUNTRY_QUARTER' ? leadTimeCountrySubcolumns : rest.subcolumns,
    };
    const titleText = {
        IMPORT_KPI_PO_ITEMS_DEVIATION: t("po_with_deviation"),
        EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE: t("transport_by_shipment_mode"),
    }
    const getSubheader = (subtype) => {
        switch (subtype) {
            case "IMPORT_KPI_PO_ITEMS_SHIPMENT":
            case "IMPORT_KPI_CONTAINER_RDD":
            case "EXPORT_KPI_EXPORT_UNITS_INVOICED":
            case "EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS":
                return data?.length && total ? <>
                    {
                        showTotal &&
                        <Typography variant="body2" data-testid={`${testId}-total`} className={subtype === 'EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS' ? classes.paddingStyle : null} >
                            {`${subtype !== 'EXPORT_KPI_EXPORT_UNITS_BY_CARRIER_STATUS' ? t("total") : t("total_count")}: ${total || 0}`}
                        </Typography>
                    }
                </> : null;
            case "IMPORT_KPI_CONTAINER_RTD": {
                const props = { ...subheaderData, ...rest, valueType: chartProps.valueType }
                return data?.length && data[0]?.cntrWithID && !loading ? <>
                    <SubheaderPattern1 {...props} />
                    <Typography variant="body1" className={classes.text} data-testid={`${testId}-without-GR`}>
                        {`${t("containers_without_GR")}: ${data[0]?.cntrWithoutGR || 0}`}
                    </Typography>
                </> : null;
            }
            case "IMPORT_KPI_SHIPMENT_CONTAINERS":
                return data?.length && data[0]?.totalShipments ? <>
                    <Typography variant="body2" data-testid={`${testId}-total`}>
                        {`${t("total_shipments")}: ${data[0]?.totalShipments || 0}`}
                    </Typography>
                </> : null;
            case "IMPORT_KPI_PO_ITEMS_DEVIATION":
            case 'EXPORT_KPI_EXPORT_UNITS_BY_SHIPMENT_MODE': {
                const props1 = { ...subheaderData, ...rest, valueType: chartProps.valueType }
                return data?.length && !loading && total ? <>
                    <SubheaderPattern2 {...props1} />
                    {
                        showTotal &&
                        <Typography variant="body1" className={classes.text} data-testid={`${testId}`}>
                            {`${(titleText[subtype])}:`}
                        </Typography>
                    }
                </> : null;
            }
            case "IMPORT_KPI_CARRIER_CONTRACT_SHIPMENT":
            case "IMPORT_KPI_CARRIER_CONTRACT_CONTAINER":
            case "IMPORT_KPI_CARRIER_OP_SHIPMENT":
            case "IMPORT_KPI_CARRIER_OP_CONTAINER":
            case "IMPORT_CONTAINER_FORECAST": {
                const props2 = { handleChange, selectedValue, ...rest }
                return !loading ? <>
                    <SubheaderPattern3 {...props2} />
                </> : null;
            }
            case "EXPORT_KPI_EXPORT_UNITS": {
                const props3 = { ...subheaderData, ...rest, valueType: chartProps.valueType }
                return data?.length && data?.find(d => d.state === 'PO_ITEMS_WITH_LD_NBR')?.totalCount && !loading ? <>
                    <Typography variant="body2" data-testid={`${testId}-export_line_items_with_load_number`} >
                        {`${t("export_line_items_with_load_number")}: ${data.find(d => d.state === 'PO_ITEMS_WITH_LD_NBR')?.totalCount || 0}`}
                    </Typography>
                    <SubheaderPattern1 {...props3} />
                    <Typography variant="body1" className={classes.text} data-testid={`${testId}-invoiced`}>
                        {`${t("exports_unit_invoiced")}(%):`}
                    </Typography>
                </> : <div className={classes.noData} />
            }
            case "EXPORT_KPI_EXPORT_UNITS_WITH_SHIPMENT": {
                const props4 = { ...subheaderData, ...rest, valueType: chartProps.valueType }
                return data?.length && data?.find(d => d.state === 'LDS_INVOICED')?.totalCount && !loading ? <>
                    <SubheaderPattern1 {...props4} />
                    <Typography variant="body1" className={classes.text} data-testid={`${testId}-with-shipment`}>
                        {`${t("exports_unit_with_shipment")}(%):`}
                    </Typography>
                </> : <div className={classes.noData} />
            }
            case "LEAD_TIME_INTERNATIONAL_BY_MONTH":
            case "LEAD_TIME_ORIGIN_BY_MONTH":
            case "LEAD_TIME_DESTINATION_BY_MONTH":
            case "LEAD_TIME_TOTAL_BY_MONTH": {
                const props5 = { handleChange, selectedValue, handleRadioValue, selectedValue1, ...rest }
                return !loading ? <>
                    <SubheaderPattern4 {...props5} />
                </> : null;
            }
            case 'LEAD_TIME_BY_COUNTRY_QUARTER': {
                const props6 = {
                    handleChange: setCountryLeadTimeView,
                    selectedValue: countryLeadTimeView,
                    text1: 'quarterly',
                    text2: 'monthly',
                    radioKey: 'quarterly',
                    radioKey1: 'monthly',
                };
                return loading ? null : (
                    <div className={classes.countryRadioLeft}>
                        <SubheaderPattern3 {...props6} />
                    </div>
                );
            }
        }
    }

    return (
        <Card className={classes.chartCard} data-testid="lao-chartscard">
            <CardHeader
                action={
                    <>
                        {refreshIcon && <IconButton data-testid={`${testId}-refresh`} size="small" onClick={() => refreshAction()}>
                            <RefreshOutlined fontSize="small" />
                        </IconButton>}
                    </>
                }
                title={<Typography variant="h6" data-testid={`${testId}-title`} >{t(title)}</Typography>}
                subheader={getSubheader(subtype || subtype1)}
            />
            {chart === "trendline" && <LaoBarChart {...chartProps} />}
            {chart === "pie" && <LaoDonutChart {...chartProps} />}
            {chart === "text" && <ShipmentContainerImportKPI {...chartProps} />}
            {chart === "process" && <LaoProcessChart {...chartProps} />}
            {chart === "grouped" && (selectedValue === "total" ? <GroupedStackBarChart {...chartProps} rotateLabel={true} /> : <GroupedBarChart {...chartProps} rotateLabel={false} />)}
            {chart === 'table' && <GroupedDataTable {...chartProps} />}
            {chart === "matrix" && <GroupedMatrixTable {...chartProps} />}
        </Card>
    );
};

export default LAOChartCard;
